/**
 * Per-route error boundary with copy-diagnostics (design §2.12).
 *
 * Two requirements it exists to satisfy:
 *
 *  1. "A global `ErrorBoundary` **per route** with 'copy diagnostics'
 *     (correlation ID + route + client version)."
 *  2. "**Route-level degradation**, not a white screen … Feature isolation is a
 *     hard requirement — one dead integration may never take down the CRM."
 *
 * TanStack Router mounts this as each route's `errorComponent`, so a throw in
 * `/contacts/:id`'s timeline leaves the shell, the nav and every other route
 * alive. `FeatureBoundary` is the same idea one level down: a panel that fails
 * is replaced by a strip inside its own column.
 */

import { Button, Card, CardContent, CardHeader, CardTitle } from '@crm/ui';
import { useRouter } from '@tanstack/react-router';
import { CopyIcon } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode, useState } from 'react';
import { buildDiagnostics, copyText, formatDiagnostics } from '../lib/diagnostics.ts';
import { toClientError } from '../lib/errors.ts';

export type RouteErrorProps = {
  readonly error: unknown;
  readonly reset?: (() => void) | undefined;
};

export function RouteErrorPanel({ error, reset }: RouteErrorProps) {
  const router = useRouter();
  const route = router.state.location.href;
  return <ErrorPanel error={error} route={route} reset={reset} />;
}

export function ErrorPanel({
  error,
  route,
  reset,
  componentStack,
}: RouteErrorProps & { route: string; componentStack?: string | undefined }) {
  const [copied, setCopied] = useState(false);
  const client = toClientError(error);
  const diagnostics = buildDiagnostics({ error, route, componentStack });

  return (
    <Card className="m-4 max-w-2xl border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive">This section failed to load</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <p className="text-muted-foreground">{client.message}</p>

        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
          <dt className="text-muted-foreground">correlation</dt>
          <dd>{diagnostics.correlationId ?? 'none — the request never reached the server'}</dd>
          <dt className="text-muted-foreground">route</dt>
          <dd className="break-all">{diagnostics.route}</dd>
          <dt className="text-muted-foreground">client</dt>
          <dd>web {diagnostics.clientVersion}</dd>
        </dl>

        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void copyText(formatDiagnostics(diagnostics)).then(setCopied);
            }}
          >
            <CopyIcon aria-hidden />
            {copied ? 'Copied' : 'Copy diagnostics'}
          </Button>
          {reset === undefined ? null : (
            <Button size="sm" onClick={reset}>
              Try again
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

type FeatureBoundaryProps = {
  readonly children: ReactNode;
  /** Named in the fallback: "LinkedIn capture" reads better than "this panel". */
  readonly feature: string;
  readonly route?: string;
};

type FeatureBoundaryState = {
  readonly error: unknown;
  readonly componentStack: string | undefined;
};

/**
 * A boundary around one panel.
 *
 * A class component because React still offers no hook equivalent of
 * `componentDidCatch`; this is the one place in the app where that is true.
 */
export class FeatureBoundary extends Component<FeatureBoundaryProps, FeatureBoundaryState> {
  override state: FeatureBoundaryState = { error: null, componentStack: undefined };

  static getDerivedStateFromError(error: unknown): Partial<FeatureBoundaryState> {
    return { error };
  }

  override componentDidCatch(_error: unknown, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? undefined });
  }

  private readonly reset = () => {
    this.setState({ error: null, componentStack: undefined });
  };

  override render(): ReactNode {
    if (this.state.error === null || this.state.error === undefined) {
      return this.props.children;
    }
    return (
      <ErrorPanel
        error={this.state.error}
        route={this.props.route ?? this.props.feature}
        componentStack={this.state.componentStack}
        reset={this.reset}
      />
    );
  }
}
